import {datadogLogs} from '@datadog/browser-logs';
import {useEffect, useMemo} from 'react';
import {isRegionInUnitedStates} from 'utils/aws-utils';
import {formatVersionForDatadog} from 'utils/datadog-utils';
import Logger from 'utils/logger';
import {getLastNameFirst} from 'utils/user-utils';
import * as EnvironmentConstants from '../constants/environment-constants';

import type {ClientRecord, Locale, User} from 'stores/session-store';

const deployment = __DEPLOYMENT__;
const environment = deployment.includes('production') ? 'production' : deployment;
const isDatadogEnabled = __IS_DATADOG_ENABLED__;
const logger = new Logger('main');

export const useDatadogLogs = (client: ClientRecord, loggedInUser: User, locale: Locale) => {
    const regionInUnitedStates = useMemo(() => isRegionInUnitedStates(__AWS_REGION__), [__AWS_REGION__]);

    useEffect(() => {
        if (!isDatadogEnabled) {
            return;
        }

        const initConfiguration = datadogLogs.getInitConfiguration();

        // Avoid reinitializing
        if (initConfiguration) {
            return;
        }

        try {
            let ddClientToken: string;
            if (environment === 'production') {
                ddClientToken = EnvironmentConstants.DATADOG_PRODUCTION_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
            } else {
                ddClientToken = EnvironmentConstants.DATADOG_DEVELOMENT_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
            }

            datadogLogs.init({
                // Adds custom attributes to all captured logs via `context`. Utils like `setGlobalContext` only work
                // when using e.g. `datadogLogs.logger.info` while this will work for e.g. `console.log` as well.
                beforeSend: (event) => {
                    event.context = {
                        deployment,
                        region: __AWS_REGION__,
                    };

                    return true; // Tells the SDK to send the log
                },
                clientToken: ddClientToken,
                env: environment,
                forwardConsoleLogs: 'all',
                service: EnvironmentConstants.SERVICE_CONSUMERS.SIMCAPTURE_CLOUD,
                sessionSampleRate: 100,
                site: 'datadoghq.com',
                version: formatVersionForDatadog(__VERSION__),
            });
        } catch (err) {
            logger.error(`Failed to initialize Datadog SDK for environment: ${environment}`, err);
        }
    }, [isDatadogEnabled, environment]);

    useEffect(() => {
        if (!regionInUnitedStates) {
            return;
        }

        if (client.get('clientId')) {
            datadogLogs.setAccount({
                id: client.get('clientId'),
                institutionName: client.get('institutionName'),
            });
        } else {
            datadogLogs.clearAccount();
        }
    }, [regionInUnitedStates, client.get('clientId')]);

    useEffect(() => {
        if (!regionInUnitedStates) {
            return;
        }

        if (loggedInUser.get('userId')) {
            datadogLogs.setUser({
                email: loggedInUser.get('email'),
                id: loggedInUser.get('userId'),
                name: getLastNameFirst(loggedInUser),
                // Custom properties
                firstName: loggedInUser.get('firstName'),
                lastName: loggedInUser.get('lastName'),
                locale,
                middleName: loggedInUser.get('middleName'),
                userName: loggedInUser.get('userName'),
            });
        } else {
            datadogLogs.clearUser();
        }
    }, [regionInUnitedStates, loggedInUser.get('userId'), locale]);
};
